import { motion } from "framer-motion";
import { Home, Building2, Palmtree, Check } from "lucide-react";

const services = [
  {
    key: "residential",
    tag: "01",
    title: "Residential",
    icon: Home,
    desc: "Weekly, bi-weekly or one-off deep cleans that make coming home feel like a fresh start.",
    points: ["Kitchens & baths", "Floors & dusting", "Deep cleans"],
    img: "https://images.unsplash.com/photo-1672667178170-99a5169570cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    span: "lg:col-span-7",
  },
  {
    key: "commercial",
    tag: "02",
    title: "Commercial",
    icon: Building2,
    desc: "Spotless offices and workspaces that keep your team healthy and your clients impressed.",
    points: ["Offices & lobbies", "Sanitizing", "Flexible hours"],
    img: "https://images.unsplash.com/photo-1592663283246-c843227611ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxTYXZhbm5haCUyMEdlb3JnaWElMjByaXZlcmZyb250JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NDQxNzA0MHww&ixlib=rb-4.1.0&q=85",
    span: "lg:col-span-5",
  },
  {
    key: "vacation",
    tag: "03",
    title: "Vacation Rentals",
    icon: Palmtree,
    desc: "Fast, five-star turnovers for Airbnb & rental hosts — linens, restock and guest-ready shine.",
    points: ["Turnover cleans", "Linen & restock", "5-star ready"],
    img: "https://images.unsplash.com/photo-1783804965042-2b74163f4666?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    span: "lg:col-span-12",
  },
];

const ease = [0.16, 1, 0.3, 1];

export default function Services() {
  return (
    <section id="services" className="px-6 py-24 md:py-36" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-xl"
          >
            What we make <span className="italic text-[#C1440E]">sparkle</span>
          </motion.h2>
          <p className="text-[#5A2E14] max-w-sm text-base">
            Three specialties, one obsessive standard of clean. Pick what fits your space.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.key}
                data-testid={`service-card-${s.key}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.75, delay: i * 0.08, ease }}
                className={`group relative overflow-hidden rounded-3xl border border-[#C68A3A] bg-[#F7E9CC] ${s.span} min-h-[320px] flex`}
              >
                <div className="relative z-10 p-8 md:p-10 flex flex-col justify-between w-full">
                  <div className="flex items-start justify-between">
                    <span className="grid place-items-center w-12 h-12 rounded-full bg-[#241B2E] text-[#EFD9AE] group-hover:bg-[#C1440E] transition-colors duration-500">
                      <Icon size={20} />
                    </span>
                    <span className="font-serif text-2xl text-[#C68A3A]">{s.tag}</span>
                  </div>
                  <div className="max-w-md">
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-10">{s.title}</h3>
                    <p className="text-[#5A2E14] mt-3 text-base leading-relaxed">{s.desc}</p>
                    <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-center gap-1.5 text-sm text-[#241B2E]">
                          <Check size={15} className="text-[#C1440E]" /> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover [mask-image:linear-gradient(to_right,transparent,black_35%)] group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
